import type { ReactElement } from 'react'
import { useCallback, useEffect, useRef } from 'react'

// // utils
import { useDialogs } from 'hooks/dialogs'
import classNames from 'classnames'
import { AnimatePresence, m } from 'framer-motion'

import { FloatingFocusManager, useFloating } from '@floating-ui/react'

const backdropClasses = 'fixed inset-0 flex items-center justify-center'

type size = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl'
type DialogSizes = size | 'custom'

// print resets
const backdropPrintOverrides = 'print:!static print:block'
const dialogPrintOverrides =
  'print:!h-auto print:!max-h-none print:!w-full print:!max-w-none print:!p-0'
const innerDialogPrintOverrides =
  'print:!p-0 print:!h-auto print:!max-h-none print:!w-full print:!max-w-none'

const theme = {
  dialog: {
    xs: {
      className:
        'relative shadow-2xl text-core-primary overflow-y-auto bg-surface-bright max-h-[90%] max-w-[90%] rounded-3xl',
      innerClassName: 'p-13 min-h-[min(350px,90vh)] md:w-[400px]',
    },
    sm: {
      className:
        'relative shadow-2xl text-core-primary overflow-y-auto bg-surface-bright max-h-[90%] max-w-[90%] rounded-3xl',
      innerClassName: 'p-13 min-h-[min(350px,90vh)] md:w-[400px]',
    },
    md: {
      className:
        'relative shadow-2xl text-core-primary overflow-y-auto bg-surface-bright max-h-[90vh] max-w-[90%] rounded-3xl',
      innerClassName:
        'p-13 min-h-[min(550px,90vh)] rounded-2xl md:w-[400px] h-full',
    },
    lg: {
      className:
        'relative shadow-2xl text-core-primary  overflow-y-auto bg-surface-bright max-h-[90%] min-w-[min(90%,800px)] max-w-[min(800px,90%)] rounded-3xl relative',
      innerClassName: 'p-13 min-h-[min(550px,90vh)] rounded-2xl',
    },
    xl: {
      className:
        'relative shadow-2xl text-core-primary overflow-y-auto bg-surface-bright max-h-[90%] min-w-[min(90%,1000px)] max-w-[min(1200px,90%)] rounded-3xl relative',
      innerClassName: 'p-13 min-h-[min(750px,90vh)] rounded-2xl',
    },
    xxl: {
      className:
        'relative shadow-2xl text-core-primary overflow-y-auto flex flex-col w-screen min-w-[100vw] max-w-[100vw] h-screen min-h-[100vh] max-h-[100vh] m-0 rounded-none bg-surface p-3',
      innerClassName: '',
    },
  },
}

function DialogDismissHandler({
  children,
  dismissOnBackdropClick,
  dismissOnEscapeKey,
  forceBackdrop,
  onDismiss,
  zIndex,
}: {
  children: React.ReactNode
  dismissOnBackdropClick: boolean
  dismissOnEscapeKey: boolean
  forceBackdrop?: boolean
  onDismiss: () => void
  zIndex?: number
}) {
  const shouldDismissOnBackdropClick = dismissOnBackdropClick !== false
  const shouldDismissOnEscapeKey = dismissOnEscapeKey !== false
  const ref = useRef<HTMLDivElement | null>(null)

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === ref.current) onDismiss()
    },
    [onDismiss]
  )

  useEffect(() => {
    if (shouldDismissOnEscapeKey) {
      const closeOnEscapeKey = (e: { key: string }) =>
        e.key === 'Escape' ? onDismiss() : null
      document.body.addEventListener('keydown', closeOnEscapeKey)
      return () => {
        document.body.removeEventListener('keydown', closeOnEscapeKey)
      }
    }
  }, [onDismiss, shouldDismissOnEscapeKey])

  return (
    <div
      ref={ref}
      className={classNames(backdropClasses, backdropPrintOverrides, {
        'bg-transparent': !forceBackdrop,
        'bg-dialog-background': forceBackdrop,
        'z-[9999]': zIndex === undefined,
      })}
      style={zIndex ? { zIndex: `${zIndex} !important` } : undefined}
      onMouseDown={
        shouldDismissOnBackdropClick ? handleBackdropClick : undefined
      }
    >
      {children}
    </div>
  )
}

export function Dialog({
  size = 'md',
  children,
  className,
  innerClassName,
  ignoreBackdropClick,
  dismissOnEscapeKey,
  testId,
  forceBackdrop,
  zIndex,
  onDismiss,
  focusTrap = true,
  unstyledWrapper = false,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
}: {
  size?: DialogSizes
  className?: string
  innerClassName?: string
  children: React.ReactNode
  ignoreBackdropClick?: boolean
  dismissOnEscapeKey?: boolean
  testId?: string
  forceBackdrop?: boolean
  zIndex?: number
  onDismiss?: () => void
  focusTrap?: boolean
  unstyledWrapper?: boolean
  'aria-label'?: string
  /** The id of the element that labels the dialog. You can use `useId` to
   * generate a unique id. Pass it here, and also set it as the `id` of the
   * label (should be an `h1` or `h2`). You can explicitely opt out by passing
   * `undefined` but be aware this is an a11y requirement. You can use
   * 'aria-label' instead but it's not recommended. Example:
   * ```
   * const headingId = useId()
   * return (
   *   <Dialog aria-labelledby={headingId}>
   *     <h1 id={headingId}>My dialog</h1>
   *   </Dialog>
   * )
   * ``` */
  'aria-labelledby': string | undefined
}) {
  // const floatingContext = useFloatingRootContext()
  const sizeClasses = size === 'custom' ? '' : theme.dialog[size].className
  const innerClasses =
    size === 'custom' ? 'h-full' : theme.dialog[size].innerClassName

  // The panel only ever receives programmatic focus (tabindex -1), e.g. when
  // the focus trap restores focus. Don't paint the browser's default ring.
  const dialogClasses = classNames('outline-none', className, {
    [sizeClasses]: !unstyledWrapper,
    [dialogPrintOverrides]: !unstyledWrapper,
  })
  const innerDialogClasses = classNames(innerClassName, {
    [innerClasses]: !unstyledWrapper,
    [innerDialogPrintOverrides]: !unstyledWrapper,
  })

  const { refs, context } = useFloating({
    open: true,
  })

  const { popDialog } = useDialogs()

  const child = (
    <div
      data-testid={testId}
      className={dialogClasses}
      aria-modal="true"
      role="dialog"
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledby}
      ref={refs.setFloating}
    >
      <div className={innerDialogClasses}>{children}</div>
    </div>
  )

  return (
    <DialogDismissHandler
      dismissOnBackdropClick={ignoreBackdropClick !== true}
      dismissOnEscapeKey={dismissOnEscapeKey === true}
      forceBackdrop={forceBackdrop}
      onDismiss={onDismiss ?? popDialog}
      zIndex={zIndex}
    >
      <FloatingFocusManager
        context={context}
        restoreFocus={true}
        disabled={focusTrap !== true}
      >
        {child as ReactElement}
      </FloatingFocusManager>
    </DialogDismissHandler>
  )
}

// A special version of a Dialog that can be used within another dialog
// It carries its own backdrop and can be used to show a dialog within a dialog
// It stack backdrops and dialogs on top of each other
export function InlineDialog({
  open = true,
  size = 'md',
  children,
  className,
  ignoreBackdropClick,
  dismissOnEscapeKey,
  innerClassName,
  testId,
  onDismiss,
  zIndex,
  focusTrap = true,
  unstyledWrapper = false,
  transitionDuration = 0.1,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
}: {
  open?: boolean
  size?: DialogSizes
  className?: string
  children: React.ReactNode
  ignoreBackdropClick?: boolean
  dismissOnEscapeKey?: boolean
  innerClassName?: string
  testId?: string
  onDismiss?: () => void
  zIndex?: number
  focusTrap?: boolean
  unstyledWrapper?: boolean
  transitionDuration?: number
  'aria-label'?: string
  /** The id of the element that labels the dialog. You can use `useId` to
   * generate a unique id. Pass it here, and also set it as the `id` of the
   * label (should be an `h1` or `h2`). You can explicitely opt out by passing
   * `undefined` but be aware this is an a11y requirement. You can use
   * 'aria-label' instead but it's not recommended. Example:
   * ```
   * const headingId = useId()
   * return (
   *   <Dialog aria-labelledby={headingId}>
   *     <h1 id={headingId}>My dialog</h1>
   *   </Dialog>
   * )
   * ``` */
  'aria-labelledby': string | undefined
}) {
  const sizeClasses = size === 'custom' ? '' : theme.dialog[size].className
  const innerClasses =
    size === 'custom' ? 'h-full' : theme.dialog[size].innerClassName

  // The panel only ever receives programmatic focus (tabindex -1), e.g. when
  // the focus trap restores focus. Don't paint the browser's default ring.
  const dialogClasses = classNames('outline-none', className, {
    [sizeClasses]: !unstyledWrapper,
    [dialogPrintOverrides]: !unstyledWrapper,
  })
  const innerDialogClasses = classNames(innerClassName, {
    [innerClasses]: !unstyledWrapper,
    [innerDialogPrintOverrides]: !unstyledWrapper,
  })

  const { refs, context } = useFloating({
    open: true,
  })

  const child = (
    <div
      data-testid={testId}
      className={dialogClasses}
      ref={refs.setFloating}
      role="dialog"
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledby}
      aria-modal="true"
    >
      <div className={innerDialogClasses}>{children}</div>
    </div>
  )

  return (
    <AnimatePresence mode={'wait'} initial={false}>
      <m.div
        key={open ? 'open' : 'closed'}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: transitionDuration }}
        exit={{ opacity: 0.2 }}
      >
        {open && (
          <DialogDismissHandler
            dismissOnBackdropClick={ignoreBackdropClick !== true}
            dismissOnEscapeKey={dismissOnEscapeKey === true}
            onDismiss={onDismiss || (() => {})}
            forceBackdrop
            zIndex={zIndex}
          >
            <FloatingFocusManager
              context={context}
              disabled={focusTrap !== true}
              restoreFocus={true}
            >
              {child as ReactElement}
            </FloatingFocusManager>
          </DialogDismissHandler>
        )}
      </m.div>
    </AnimatePresence>
  )
}
